import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Info, Loader2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { reportingApi, facilitiesApi, CpmReportFacility, CpmReportSlideshow } from '@/lib/api';
import { useAuth } from '@/store/auth';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCycle(seconds: number): string {
  if (seconds === 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatDuration(seconds: number): string {
  if (seconds === 0) return '—';
  return `${seconds}s`;
}

// ── CSV Export ────────────────────────────────────────────────────────────────

function buildCsv(facilities: CpmReportFacility[]): string {
  const rows: string[][] = [];
  rows.push([
    'Facility',
    'Slideshow',
    'Default',
    'Cycle Time',
    'Ad / Sponsor',
    'Slot Duration (s)',
    'Exposures per 30 min',
    'Exposures per 1 hr',
    'Exposures per 2 hr',
    'Exposures per 3 hr',
  ]);

  for (const facility of facilities) {
    for (const sw of facility.slideshows) {
      if (sw.images.length === 0) {
        rows.push([
          facility.facilityName,
          sw.name,
          sw.isDefault ? 'Yes' : 'No',
          formatCycle(sw.cycleSeconds),
          '(no images)',
          '',
          String(sw.rates.per30min),
          String(sw.rates.per1hr),
          String(sw.rates.per2hr),
          String(sw.rates.per3hr),
        ]);
      } else {
        for (const img of sw.images) {
          rows.push([
            facility.facilityName,
            sw.name,
            sw.isDefault ? 'Yes' : 'No',
            formatCycle(sw.cycleSeconds),
            img.originalName,
            String(img.duration),
            String(sw.rates.per30min),
            String(sw.rates.per1hr),
            String(sw.rates.per2hr),
            String(sw.rates.per3hr),
          ]);
        }
      }
    }
  }

  return rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
}

function downloadCsv(csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cpm-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Slideshow Row Group ───────────────────────────────────────────────────────

function SlideshowGroup({ sw }: { sw: CpmReportSlideshow; facilityName: string }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Slideshow header row */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="flex w-full items-center gap-3 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 dark:bg-gray-800/60 dark:hover:bg-gray-800"
      >
        {expanded
          ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
          : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
        <span className="flex-1 font-semibold text-gray-800 dark:text-gray-100">{sw.name}</span>
        {sw.isDefault && (
          <Badge className="border-0 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Default</Badge>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Cycle: <span className="font-medium text-gray-700 dark:text-gray-200">{formatCycle(sw.cycleSeconds)}</span>
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {sw.images.length} ad{sw.images.length !== 1 ? 's' : ''}
        </span>
      </button>

      {expanded && (
        sw.images.length === 0 ? (
          <div className="px-6 py-4 text-sm text-gray-400 dark:text-gray-500 italic">
            No active images in this slideshow.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <th className="px-6 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Ad / Sponsor</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Slot</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Per 30 min</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Per 1 hr</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Per 2 hr</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Per 3 hr</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {sw.images.map((img, i) => (
                <tr key={img.id} className={`${i % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/20'} hover:bg-blue-50/40 dark:hover:bg-blue-900/10`}>
                  <td className="px-6 py-3 font-medium text-gray-800 dark:text-gray-200">
                    {img.originalName}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                    {formatDuration(img.duration)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                    {sw.rates.per30min}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                    {sw.rates.per1hr}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                    {sw.rates.per2hr}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                    {sw.rates.per3hr}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AdminReportsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [facilityFilter, setFacilityFilter] = useState<string>('');

  const { data: facilitiesData } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: () => facilitiesApi.list({ limit: 100 }),
    enabled: isAdmin,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['cpm-report', facilityFilter],
    queryFn: () => reportingApi.getCpm(facilityFilter ? { facilityId: facilityFilter } : undefined),
    staleTime: 60000,
  });

  const facilities = data?.facilities ?? [];
  const allFacilities = facilitiesData?.facilities ?? [];

  // Totals across all visible facilities
  const totalSlideshows = facilities.reduce((n, f) => n + f.slideshows.length, 0);
  const totalAds = facilities.reduce((n, f) => n + f.slideshows.reduce((m, s) => m + s.images.length, 0), 0);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">CPM / Exposure Rate Report</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Estimated ad exposures based on slideshow rotation settings.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={facilities.length === 0}
          onClick={() => downloadCsv(buildCsv(facilities))}
        >
          <Download className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
      </div>

      {/* Disclaimer */}
      <div className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-900/20">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Exposure counts are estimated from slideshow rotation settings only.
          To calculate total ad impressions and CPM value, multiply by the number of streams
          and estimated concurrent viewership for each event.
        </p>
      </div>

      {/* Filters */}
      {isAdmin && allFacilities.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Facility</label>
          <select
            value={facilityFilter}
            onChange={e => setFacilityFilter(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">All Facilities</option>
            {allFacilities.map((f: any) => (
              <option key={f.facilityId} value={f.facilityId}>{f.name}</option>
            ))}
          </select>
          {facilityFilter && (
            <button onClick={() => setFacilityFilter('')} className="text-xs text-blue-500 hover:text-blue-700">
              Clear
            </button>
          )}
        </div>
      )}

      {/* Summary pills */}
      {!isLoading && !error && (
        <div className="flex gap-3">
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs text-gray-500 dark:text-gray-400">Facilities</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{facilities.length}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs text-gray-500 dark:text-gray-400">Active Slideshows</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{totalSlideshows}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Ad Slots</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{totalAds}</p>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          Failed to load report data.
        </div>
      ) : facilities.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 py-16 text-center dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">No active facilities or slideshows found.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {facilities.map(facility => (
            <div key={facility.facilityId}>
              {/* Facility heading */}
              <div className="mb-3 flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">{facility.facilityName}</h3>
                <span className="text-xs text-gray-400 dark:text-gray-500">{facility.facilityId}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">·</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {facility.slideshows.length} slideshow{facility.slideshows.length !== 1 ? 's' : ''}
                </span>
              </div>

              {facility.slideshows.length === 0 ? (
                <p className="px-1 text-sm italic text-gray-400 dark:text-gray-500">No active slideshows at this facility.</p>
              ) : (
                <div className="space-y-3">
                  {facility.slideshows.map(sw => (
                    <SlideshowGroup key={sw.id} sw={sw} facilityName={facility.facilityName} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Column legend */}
      {!isLoading && !error && totalAds > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/40">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Column Guide</p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs text-gray-600 dark:text-gray-400 sm:grid-cols-3">
            <span><strong className="text-gray-700 dark:text-gray-300">Slot</strong> — how long this image displays per rotation</span>
            <span><strong className="text-gray-700 dark:text-gray-300">Cycle Time</strong> — total time for one full slideshow rotation</span>
            <span><strong className="text-gray-700 dark:text-gray-300">Per 30 min / 1 hr / 2 hr / 3 hr</strong> — estimated full rotations in that duration</span>
          </div>
        </div>
      )}
    </div>
  );
}
